import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader, Toast, Modal } from "@/components/Layout";
import api from "@/lib/api";

const errText = (e) => e?.response?.data?.detail || "Eroare";
const NORMED_IN = { stat: "Statul de funcții", fisa: "Fișa postului", stat_sau_fisa: "Stat sau fișă" };
const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const TABS = [["general", "General"], ["coef", "Coeficienți (Anexa 9)"], ["norme", "Norme minime"], ["formatiuni", "Dimensiuni formațiuni"]];

function GeneralTab({ year, onSaved, toast }) {
  const [f, setF] = useState(year);
  useEffect(() => setF(year), [year]);
  const set = (k, v) => setF({ ...f, [k]: v });
  const num = (k, label, hint) => (
    <div><label className="label">{label}</label>
      <input className="input" type="number" value={f[k]} onChange={e => set(k, e.target.value === "" ? "" : Number(e.target.value))} data-testid={`sfd-gen-${k}`} />
      {hint && <div style={{ fontSize: 11, color: "#64748B", marginTop: 3 }}>{hint}</div>}</div>);
  const annual = (Number(f.working_days) - Number(f.leave_days_min)) * Number(f.hours_per_day);
  const save = async () => {
    try {
      const body = { ...f }; delete body.id; delete body.name; delete body.is_current; delete body.annual_hours; delete body.created_at; delete body.updated_at; delete body.counts;
      const r = await api.put(`/stat-didactic/years/${year.id}`, body);
      toast("Parametri generali salvați", "success"); onSaved(r.data);
    } catch (e) { toast(errText(e), "error"); }
  };
  return (
    <div className="card" style={{ padding: 22 }}>
      <div className="grid-3">
        <div><label className="label">Început an universitar</label><input className="input" type="date" value={f.start_date} onChange={e => set("start_date", e.target.value)} /></div>
        <div><label className="label">Sfârșit an universitar</label><input className="input" type="date" value={f.end_date} onChange={e => set("end_date", e.target.value)} /></div>
        <div><label className="label">Norme minime după</label>
          <select className="select" value={f.norm_basis} onChange={e => set("norm_basis", e.target.value)} data-testid="sfd-gen-norm_basis">
            <option value="senat">Hotărârea Senatului (majorate)</option><option value="lege">Legea învățământului superior</option></select></div>
        {num("weeks_per_year", "Săptămâni pe an universitar", "Statele se întocmesc pe 28 de săptămâni")}
        {num("weeks_per_semester", "Săptămâni pe semestru")}
        {num("hours_per_day", "Ore lucrătoare pe zi")}
        {num("working_days", "Zile lucrătoare în an", "Fără sărbători legale, sâmbete și duminici")}
        {num("leave_days_min", "Zile de concediu (minimum)")}
        {num("legal_holidays_weekdays", "Sărbători legale în timpul săptămânii")}
        {num("max_norm_hours_week", "Norma didactică maximă (ore conv./săptămână)")}
        {num("extra_applicative_without_course", "Majorare dacă postul nu are curs (ore conv.)")}
      </div>
      <div style={{ marginTop: 14, fontSize: 13 }}>Ore anuale în fișa postului: <b data-testid="sfd-gen-annual">{annual}</b> <span style={{ color: "#64748B" }}>(zile lucrătoare − concediu) × ore pe zi</span></div>
      <div style={{ marginTop: 14 }}><label className="label">Note</label>
        <textarea className="textarea" rows={2} value={f.notes} onChange={e => set("notes", e.target.value)} /></div>
      <div style={{ marginTop: 16 }}><button className="btn btn-primary" onClick={save} data-testid="sfd-gen-save">Salvează</button></div>
    </div>
  );
}

function CoefTab({ yearId, toast }) {
  const [rows, setRows] = useState(null);
  const [draft, setDraft] = useState({});             // id -> câmpuri modificate
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [adding, setAdding] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const load = () => api.get(`/stat-didactic/years/${yearId}/coefficients`).then(r => { setRows(r.data); setDraft({}); });
  useEffect(() => { load(); }, [yearId]);

  const view = useMemo(() => (rows || []).filter(r => (!kind || r.norm_kind === kind) &&
    (!q || (r.code + " " + r.name).toLowerCase().includes(q.toLowerCase()))), [rows, q, kind]);
  const val = (r, k) => (draft[r.id] && k in draft[r.id] ? draft[r.id][k] : r[k]);
  const edit = (r, k, v) => setDraft({ ...draft, [r.id]: { ...(draft[r.id] || {}), [k]: v } });
  const dirty = Object.keys(draft);

  const saveAll = async () => {
    try {
      for (const id of dirty) {
        const r = rows.find(x => x.id === id);
        await api.put(`/stat-didactic/years/${yearId}/coefficients/${id}`, {
          coefficient: Number(val(r, "coefficient")), max_hours_year: numOrNull(val(r, "max_hours_year")),
          normed_in: val(r, "normed_in"), notes: val(r, "notes"), active: val(r, "active") });
      }
      toast(dirty.length === 1 ? "Activitate salvată" : `${dirty.length} activități salvate`, "success"); load();
    } catch (e) { toast(errText(e), "error"); }
  };
  const create = async () => {
    try {
      await api.post(`/stat-didactic/years/${yearId}/coefficients`, { ...adding, coefficient: Number(adding.coefficient), max_hours_year: numOrNull(adding.max_hours_year) });
      setAdding(null); toast("Activitate adăugată", "success"); load();
    } catch (e) { toast(errText(e), "error"); }
  };
  const remove = async () => {
    try { await api.delete(`/stat-didactic/years/${yearId}/coefficients/${deleting.id}`); setDeleting(null); toast("Activitate ștearsă", "success"); load(); }
    catch (e) { toast(errText(e), "error"); setDeleting(null); }
  };
  if (!rows) return <div>Se încarcă...</div>;
  let lastChapter = null;
  return (
    <>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <input className="input" style={{ maxWidth: 260 }} placeholder="Caută după cod sau denumire" value={q} onChange={e => setQ(e.target.value)} data-testid="sfd-coef-search" />
        <select className="select" style={{ maxWidth: 200 }} value={kind} onChange={e => setKind(e.target.value)}>
          <option value="">Toată norma</option><option value="didactica">Norma didactică</option><option value="cercetare">Norma de cercetare</option></select>
        <span style={{ flex: 1 }} />
        <button className="btn btn-secondary" onClick={() => setAdding({ code: "", name: "", chapter: "", chapter_title: "", norm_kind: "didactica", coefficient: 0.5, max_hours_year: "", normed_in: "fisa", notes: "", active: true })} data-testid="sfd-coef-add">+ Activitate</button>
        <button className="btn btn-primary" disabled={dirty.length === 0} onClick={saveAll} data-testid="sfd-coef-save">Salvează modificările{dirty.length ? ` (${dirty.length})` : ""}</button>
      </div>
      <div className="card" style={{ overflowX: "auto" }}>
        <table className="tbl" style={{ minWidth: 980 }}>
          <thead><tr><th>Cod</th><th>Activitate</th><th title="Ore convenționale pentru o oră fizică">Coeficient</th><th>Plafon ore/an</th><th>Se normează în</th><th>Activ</th><th></th></tr></thead>
          <tbody>{view.map(r => {
            const head = r.chapter !== lastChapter ? (lastChapter = r.chapter, true) : false;
            return (
              <React.Fragment key={r.id}>
                {head && <tr><td colSpan={7} style={{ background: "#F1F5F9", fontWeight: 700, fontSize: 12 }}>{r.chapter ? `${r.chapter} — ` : ""}{r.chapter_title}</td></tr>}
                <tr style={draft[r.id] ? { background: "#FFFBEB" } : undefined} data-testid={`sfd-coef-${r.code}`}>
                  <td style={{ fontWeight: 700 }}>{r.code}</td>
                  <td style={{ fontSize: 12.5, maxWidth: 420 }} title={r.notes}>{r.name}</td>
                  <td><input className="input" type="number" step="0.05" min="0" style={{ width: 84 }} value={val(r, "coefficient")} onChange={e => edit(r, "coefficient", e.target.value)} data-testid={`sfd-coef-input-${r.code}`} /></td>
                  <td><input className="input" type="number" min="0" style={{ width: 84 }} value={val(r, "max_hours_year") ?? ""} onChange={e => edit(r, "max_hours_year", e.target.value)} placeholder="—" /></td>
                  <td><select className="select" value={val(r, "normed_in")} onChange={e => edit(r, "normed_in", e.target.value)}>{Object.entries(NORMED_IN).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></td>
                  <td><input type="checkbox" checked={!!val(r, "active")} onChange={e => edit(r, "active", e.target.checked)} /></td>
                  <td><button className="btn btn-ghost" style={{ padding: "2px 8px", fontSize: 12, color: "#DC2626" }} onClick={() => setDeleting(r)}>Șterge</button></td>
                </tr>
              </React.Fragment>);
          })}</tbody>
        </table>
        {view.length === 0 && <div style={{ padding: 16, color: "#94A3B8", fontSize: 13 }}>Nicio activitate pentru filtrul ales.</div>}
      </div>
      <div style={{ fontSize: 11.5, color: "#64748B", marginTop: 8 }}>Mențiunile din Anexa 9 se văd ținând cursorul peste denumirea activității. Coeficientul arată câte ore convenționale se obțin dintr-o oră fizică.</div>
      {adding && (
        <Modal title="Activitate nouă" onClose={() => setAdding(null)} width={620}>
          <div className="grid-2">
            <div><label className="label label-req">Cod (ex. F32)</label><input className="input" value={adding.code} onChange={e => setAdding({ ...adding, code: e.target.value })} data-testid="sfd-coef-new-code" /></div>
            <div><label className="label">Capitol (literă)</label><input className="input" maxLength={2} value={adding.chapter} onChange={e => setAdding({ ...adding, chapter: e.target.value.toUpperCase() })} /></div>
            <div style={{ gridColumn: "1 / -1" }}><label className="label label-req">Activitate</label><input className="input" value={adding.name} onChange={e => setAdding({ ...adding, name: e.target.value })} data-testid="sfd-coef-new-name" /></div>
            <div><label className="label">Norma</label><select className="select" value={adding.norm_kind} onChange={e => setAdding({ ...adding, norm_kind: e.target.value })}><option value="didactica">Didactică</option><option value="cercetare">Cercetare</option></select></div>
            <div><label className="label">Se normează în</label><select className="select" value={adding.normed_in} onChange={e => setAdding({ ...adding, normed_in: e.target.value })}>{Object.entries(NORMED_IN).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
            <div><label className="label label-req">Coeficient</label><input className="input" type="number" step="0.05" min="0" value={adding.coefficient} onChange={e => setAdding({ ...adding, coefficient: e.target.value })} /></div>
            <div><label className="label">Plafon ore/an</label><input className="input" type="number" min="0" value={adding.max_hours_year} onChange={e => setAdding({ ...adding, max_hours_year: e.target.value })} /></div>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
            <button className="btn btn-secondary" onClick={() => setAdding(null)}>Anulează</button>
            <button className="btn btn-primary" onClick={create} data-testid="sfd-coef-new-save">Adaugă</button>
          </div>
        </Modal>)}
      {deleting && (
        <Modal title={`Șterge activitatea ${deleting.code}?`} onClose={() => setDeleting(null)} width={460}>
          <div style={{ fontSize: 13, color: "#334155", marginBottom: 16 }}>{deleting.name}</div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn-secondary" onClick={() => setDeleting(null)}>Anulează</button>
            <button className="btn btn-danger" onClick={remove}>Șterge</button>
          </div>
        </Modal>)}
    </>
  );
}

function NormsTab({ yearId, year, labels, toast }) {
  const [rows, setRows] = useState(null);
  const load = () => api.get(`/stat-didactic/years/${yearId}/norms`).then(r => setRows(r.data));
  useEffect(() => { load(); }, [yearId]);
  if (!rows) return <div>Se încarcă...</div>;
  const set = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const save = async () => {
    try {
      await api.put(`/stat-didactic/years/${yearId}/norms`, rows.map(r => ({
        function_key: r.function_key, min_law: Number(r.min_law), min_course_law: Number(r.min_course_law),
        min_senate: Number(r.min_senate), min_course_senate: Number(r.min_course_senate),
        min_course_with_residents: numOrNull(r.min_course_with_residents) })));
      toast("Norme salvate", "success"); load();
    } catch (e) { toast(errText(e), "error"); }
  };
  const cell = (i, k, w = 84) => <input className="input" type="number" step="0.5" min="0" style={{ width: w }} value={rows[i][k] ?? ""} onChange={e => set(i, k, e.target.value)} />;
  return (
    <div className="card" style={{ padding: 22, overflowX: "auto" }}>
      <div style={{ fontSize: 12.5, color: "#475569", marginBottom: 12 }}>
        Norma didactică minimă pe săptămână, în ore convenționale. Se aplică norma <b>{year.norm_basis === "senat" ? "aprobată de Senat" : "din lege"}</b> (se schimbă din fila General);
        norma maximă este {year.max_norm_hours_week} ore convenționale pe săptămână.
      </div>
      <table className="tbl" style={{ minWidth: 760 }}>
        <thead><tr><th>Funcția didactică</th><th>Normă minimă (lege)</th><th>din care curs</th><th>Normă minimă (Senat)</th><th>din care curs</th><th>Curs minim, dacă are rezidenți</th></tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={r.id} data-testid={`sfd-norm-${r.function_key}`}>
            <td style={{ fontWeight: 600 }}>{labels?.functions?.[r.function_key] || r.function_key}</td>
            <td>{cell(i, "min_law")}</td><td>{cell(i, "min_course_law")}</td><td>{cell(i, "min_senate")}</td><td>{cell(i, "min_course_senate")}</td><td>{cell(i, "min_course_with_residents", 100)}</td>
          </tr>))}</tbody>
      </table>
      <div style={{ marginTop: 16 }}><button className="btn btn-primary" onClick={save} data-testid="sfd-norms-save">Salvează</button></div>
    </div>
  );
}

function SizesTab({ yearId, labels, toast }) {
  const [rows, setRows] = useState(null);
  const [dirty, setDirty] = useState({});
  const ORDER_T = { L: 0, M: 1, D: 2, R: 3 };
  const ORDER_K = { serie: 0, grupa_clinic: 1, grupa_neclinic: 2, grupa_complementar: 3, grupa_seminar: 4, grupa_aplicativ: 5 };
  const load = () => api.get(`/stat-didactic/years/${yearId}/formation-sizes`).then(r => {
    setRows([...r.data].sort((a, b) => ORDER_T[a.study_type] - ORDER_T[b.study_type] || ORDER_K[a.kind] - ORDER_K[b.kind])); setDirty({}); });
  useEffect(() => { load(); }, [yearId]);
  if (!rows) return <div>Se încarcă...</div>;
  const set = (id, k, v) => { setRows(rows.map(r => (r.id === id ? { ...r, [k]: v } : r))); setDirty({ ...dirty, [id]: true }); };
  const save = async () => {
    try {
      for (const id of Object.keys(dirty)) {
        const r = rows.find(x => x.id === id);
        await api.put(`/stat-didactic/years/${yearId}/formation-sizes/${id}`, {
          min_students: numOrNull(r.min_students), max_students: numOrNull(r.max_students), aracis_max: numOrNull(r.aracis_max), notes: r.notes || "" });
      }
      toast("Dimensiuni salvate", "success"); load();
    } catch (e) { toast(errText(e), "error"); }
  };
  const inp = (r, k) => <input className="input" type="number" min="0" style={{ width: 84 }} value={r[k] ?? ""} onChange={e => set(r.id, k, e.target.value)} placeholder="—" />;
  return (
    <div className="card" style={{ padding: 22, overflowX: "auto" }}>
      <div style={{ fontSize: 12.5, color: "#475569", marginBottom: 12 }}>
        Câți studenți are o serie sau o grupă (regulament, art. 4). Peste <b>plafonul ARACIS</b> platforma doar avertizează; limitele din regulament sunt cele după care se propun formațiunile.
      </div>
      <table className="tbl" style={{ minWidth: 1000 }}>
        <thead><tr><th>Studii</th><th>Formațiune</th><th>Minim</th><th>Maxim</th><th>Plafon ARACIS</th><th>Observații</th></tr></thead>
        <tbody>{rows.map(r => (
          <tr key={r.id} data-testid={`sfd-size-${r.study_type}-${r.kind}`}>
            <td>{labels?.study_types?.[r.study_type] || r.study_type}</td>
            <td style={{ fontWeight: 600 }}>{labels?.formation_kinds?.[r.kind] || r.kind}</td>
            <td>{inp(r, "min_students")}</td><td>{inp(r, "max_students")}</td><td>{inp(r, "aracis_max")}</td>
            <td><input className="input" style={{ minWidth: 300 }} value={r.notes || ""} onChange={e => set(r.id, "notes", e.target.value)} /></td>
          </tr>))}</tbody>
      </table>
      <div style={{ marginTop: 16 }}><button className="btn btn-primary" disabled={Object.keys(dirty).length === 0} onClick={save} data-testid="sfd-sizes-save">Salvează</button></div>
    </div>
  );
}

export default function YearParams() {
  const { id } = useParams();
  const [year, setYear] = useState(null);
  const [labels, setLabels] = useState(null);
  const [tab, setTab] = useState("general");
  const [msg, setMsg] = useState({ text: "", type: "info" });
  const toast = (text, type = "info") => { setMsg({ text, type }); setTimeout(() => setMsg({ text: "" }), 3000); };
  useEffect(() => {
    api.get(`/stat-didactic/years/${id}`).then(r => setYear(r.data)).catch(() => setYear(false));
    api.get("/stat-didactic/years/labels").then(r => setLabels(r.data)).catch(() => {});
  }, [id]);
  if (year === null) return <div>Se încarcă...</div>;
  if (year === false) return <div>An universitar inexistent. <Link to="/stat-didactic/ani">Înapoi la ani universitari</Link></div>;
  return (
    <>
      <PageHeader title={`Parametri ${year.name}`} subtitle={year.is_current ? "An curent" : "Parametrii anului universitar"}
        actions={<Link className="btn btn-secondary" to="/stat-didactic/ani">← Ani universitari</Link>} />
      <Toast msg={msg.text} type={msg.type} />
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
        {TABS.map(([k, l]) => <button key={k} className={"btn " + (tab === k ? "btn-primary" : "btn-secondary")} onClick={() => setTab(k)} data-testid={`sfd-tab-${k}`}>{l}</button>)}
      </div>
      {tab === "general" && <GeneralTab year={year} onSaved={(y) => setYear({ ...year, ...y })} toast={toast} />}
      {tab === "coef" && <CoefTab yearId={id} toast={toast} />}
      {tab === "norme" && <NormsTab yearId={id} year={year} labels={labels} toast={toast} />}
      {tab === "formatiuni" && <SizesTab yearId={id} labels={labels} toast={toast} />}
    </>
  );
}
